import React, { useState } from "react";
import {
  IconBuildingSkyscraper,
  IconArrowLeft,
  IconArrowRight,
} from "@tabler/icons-react";
import PhoneInput, {
  getCountries,
  isValidPhoneNumber,
  type Country,
} from "react-phone-number-input";
import phoneCountryLabels from "react-phone-number-input/locale/en.json";
import "react-phone-number-input/style.css";
import SectionCard from "./SectionCard";
import type { ICompanyDetails, CountryCode } from "../../store/@types/portal";
import { BUSINESS_TYPES, COUNTRIES } from "../../constants/countryData";
import TextInput from "../elements/form/TextInput";
import SelectInput from "../elements/form/SelectInput";
import TextBox from "../elements/form/TextBox";
import Button from "../elements/form/Button";
import type { ITextInputErrorWarning } from "../../store/@types/misc";

const normalizeCountryName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const phoneCountryByName = new Map<string, Country>(
  getCountries().map((phoneCountry) => [
    normalizeCountryName(phoneCountryLabels[phoneCountry] || ""),
    phoneCountry,
  ]),
);

type ICompanyDetailsStep = {
  countryCode: CountryCode | "";
  countryName: string;
  details: ICompanyDetails;
  creatingApplication?: boolean;
  onChange: (
    field: keyof ICompanyDetails,
    value: ICompanyDetails[keyof ICompanyDetails],
  ) => void;
  onBack: () => void;
  onContinue: () => void;
};

type CompanyDetailsErrors = Partial<
  Record<keyof ICompanyDetails, string>
>;

const validateCompanyDetails = (
  details: ICompanyDetails,
): CompanyDetailsErrors => {
  const errors: CompanyDetailsErrors = {};

  if (!details.company_name.trim()) {
    errors.company_name = "Enter a company name.";
  }
  if (!details.business_type) {
    errors.business_type = "Select a business type.";
  }
  if (!details.company_address.trim()) {
    errors.company_address = "Enter the registered address.";
  }
  if (!details.contact_email.trim()) {
    errors.contact_email = "Enter a contact email address.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.contact_email.trim())) {
    errors.contact_email = "Enter a valid email address.";
  }
  if (!details.contact_phone) {
    errors.contact_phone = "Enter a contact phone number.";
  } else if (!isValidPhoneNumber(details.contact_phone)) {
    errors.contact_phone = "Enter a valid phone number.";
  }
  if (!details.share_capital.trim()) {
    errors.share_capital = "Enter the share capital.";
  } else if (
    !Number.isFinite(Number(details.share_capital)) ||
    Number(details.share_capital) <= 0
  ) {
    errors.share_capital = "Share capital must be greater than zero.";
  }
  if (!Number.isInteger(details.num_directors) || details.num_directors < 1) {
    errors.num_directors = "Enter at least one director.";
  }
  if (!details.business_activity.trim()) {
    errors.business_activity = "Describe the nature of the business.";
  }

  return errors;
};

const CompanyDetailsStep: React.FC<ICompanyDetailsStep> = ({
  countryCode,
  countryName,
  details,
  creatingApplication = false,
  onChange,
  onBack,
  onContinue,
}) => {
  const [continueAttempted, setContinueAttempted] = useState(false);
  const country = COUNTRIES.find((c) => c.code === countryCode);
  const phoneDefaultCountry = phoneCountryByName.get(
    normalizeCountryName(countryName),
  );
  const errors = continueAttempted
    ? validateCompanyDetails(details)
    : {};

  const handleContinue = () => {
    setContinueAttempted(true);
    if (Object.keys(validateCompanyDetails(details)).length === 0) {
      onContinue();
    }
  };

  const getFieldStatus = (
    field: keyof ICompanyDetails,
  ): ITextInputErrorWarning => (errors[field] ? "warning" : "");

  const renderError = (field: keyof ICompanyDetails) =>
    errors[field] ? (
      <p className="mt-1 text-sm text-red-600" role="alert">
        {errors[field]}
      </p>
    ) : null;

  return (
    <SectionCard
      icon={<IconBuildingSkyscraper size={20} stroke={1.75} />}
      title={`Company Information${countryName ? ` — ${countryName}` : ""}`}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <TextInput
              label="Company Name"
              name="companyName"
              type="text"
              value={details.company_name}
              placeholder="Enter desired company name"
              required
              status={getFieldStatus("company_name")}
              onChange={(e) => onChange("company_name", e.target.value)}
            />
            {renderError("company_name")}
          </div>
          <div>
            <SelectInput
              label="Business Type"
              name="businessType"
              status={getFieldStatus("business_type")}
              items={BUSINESS_TYPES.map((type) => ({
                id: type.id,
                name: type.name,
              }))}
              value={details.business_type}
              onChange={(e) => onChange("business_type", e.target.value)}
            />
            {renderError("business_type")}
          </div>
        </div>

        <div>
          <TextBox
            label="Registered Address"
            name="registeredAddress"
            value={details.company_address}
            placeholder="Full business address"
            status={getFieldStatus("company_address")}
            rowsValue={3}
            onChange={(e) => onChange("company_address", e.target.value)}
          />
          {renderError("company_address")}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <TextInput
              label="Contact Email"
              name="contactEmail"
              type="email"
              value={details.contact_email}
              placeholder="business@example.com"
              required
              status={getFieldStatus("contact_email")}
              onChange={(e) => onChange("contact_email", e.target.value)}
            />
            {renderError("contact_email")}
          </div>
          <div className="w-full">
            <label htmlFor="contact_phone" className="text-md font-medium">
              Contact Phone
            </label>
            <div
              className={`mt-1 flex h-11 w-full items-center rounded-lg border px-3 py-2 text-gray-900 focus-within:border-primary-500 focus-within:ring focus-within:ring-primary-100/30 ${
                errors.contact_phone ? "border-[red]" : "border-[#e5e7eb]"
              }`}
            >
              <PhoneInput
                key={`${countryName}-${phoneDefaultCountry || ""}`}
                id="contact_phone"
                defaultCountry={phoneDefaultCountry}
                international
                countryCallingCodeEditable={false}
                value={details.contact_phone || undefined}
                onChange={(value) => onChange("contact_phone", value ?? "")}
                numberInputProps={{
                  name: "contact_phone",
                  required: true,
                  placeholder: "Phone Number",
                  "aria-invalid": Boolean(errors.contact_phone),
                  className:
                    "PhoneInputInput w-full !outline-none px-0 py-0 text-sm border-none focus:border-transparent focus:ring-0",
                }}
                className="PhoneInput flex-1 gap-2"
              />
            </div>
            {renderError("contact_phone")}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <TextInput
              label={`Share Capital${country?.currencyCode ? ` (${country.currencyCode})` : ""}`}
              name="shareCapital"
              type="number"
              value={details.share_capital}
              placeholder="10000"
              required
              status={getFieldStatus("share_capital")}
              onChange={(e) => onChange("share_capital", e.target.value)}
            />
            {renderError("share_capital")}
          </div>
          <div>
            <TextInput
              label="Number of Directors"
              name="numberOfDirectors"
              type="number"
              value={String(details.num_directors)}
              placeholder="2"
              required
              status={getFieldStatus("num_directors")}
              onChange={(e) =>
                onChange("num_directors", Number(e.target.value) || 0)
              }
            />
            {renderError("num_directors")}
          </div>
        </div>

        <div>
          <TextBox
            label="Nature of Business"
            name="natureOfBusiness"
            value={details.business_activity}
            placeholder="Describe your business activities"
            status={getFieldStatus("business_activity")}
            rowsValue={3}
            onChange={(e) => onChange("business_activity", e.target.value)}
          />
          {renderError("business_activity")}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between pt-6">
        <Button
          title="Back"
          type="button"
          onHandler={onBack}
          icon={<IconArrowLeft size={18} />}
          bg_color="bg-white"
          bg_hoverColor="bg-slate-50"
          text_color="text-slate-700"
          className="rounded-full border border-slate-200"
        />
        <Button
          title="Continue to AI Assistant"
          type="button"
          onHandler={handleContinue}
          loading={creatingApplication}
          rightIcon={<IconArrowRight size={18} />}
          bg_color="bg-primary-500"
          bg_hoverColor="bg-primary-600"
          className="rounded-full"
        />
      </div>
    </SectionCard>
  );
};

export default CompanyDetailsStep;
